import { Cubit } from "blac";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { TranslationKey } from "src/types/translationKey";

export interface BlockingLoadingOverlayErrorProps {
  title?: string;
  message?: string;
  retry?: () => void;
  onCancel?: () => void;
  cancelText?: TranslationKey;
}

export interface BlockingLoadingOverlayState {
  status: "error" | "loading" | "none" | "success";
  error?: BlockingLoadingOverlayErrorProps;
  bg: "solid" | "transparent" | "branded" | "partner" | "biometric";
  fadeIn: boolean;
  title?: string | TranslationKey;
  message?: string | TranslationKey;
  progress?: number; // 0-100, undefined = no progress bar
  progressIndeterminate?: boolean; // true = spinner only, false = progress bar
  progressLabel?: string; // e.g., "45 MB / 100 MB"
  onCancel?: () => void; // Callback for cancel button during progress
  cancelText?: TranslationKey;
}

export class BlockingLoadingOverlayBloc extends Cubit<BlockingLoadingOverlayState> {
  constructor() {
    super({
      status: "none",
      bg: "branded",
      fadeIn: true
    });
  }
  loadingCounter = 0;
  longLoadingReportTimeout: ReturnType<typeof setTimeout> | null = null;

  public readonly endLoading = () => {
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    if (this.loadingCounter === 0) {
      this.emit({
        ...this.state,
        status: "none",
        title: "",
        message: "",
        progress: undefined,
        progressIndeterminate: undefined,
        progressLabel: undefined,
        onCancel: undefined
      });

      if (this.longLoadingReportTimeout) {
        clearTimeout(this.longLoadingReportTimeout);
      }
    }
  };

  public readonly loadingSuccess = (
    options: {
      title?: string | TranslationKey;
      message?: string | TranslationKey;
    } = {}
  ) => {
    this.emit({
      ...this.state,
      status: "success",
      title: options.title ?? "",
      message: options.message ?? "",
      progress: undefined,
      progressIndeterminate: undefined,
      progressLabel: undefined,
      onCancel: undefined
    });

    let timeout = 1400;
    if (options.title) {
      timeout += 600;
    }
    if (options.message) {
      timeout += 600;
    }

    setTimeout(() => {
      this.endLoading();
    }, timeout);
  };

  public readonly loadingError = (error?: BlockingLoadingOverlayErrorProps) => {
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    this.emit({
      ...this.state,
      status: "error",
      error,
      title: error?.title ?? "",
      message: error?.message ?? "",
      progress: undefined,
      progressIndeterminate: undefined,
      progressLabel: undefined,
      onCancel: undefined,
      cancelText: error?.cancelText ?? "cancel"
    });
    addSentryBreadcrumb(
      "loading.overlay",
      `Loading error: ${error?.title ?? "No title"}`
    );
    this.loadingCounter = Math.max(this.loadingCounter - 1, 0);
    if (this.loadingCounter === 0 && this.longLoadingReportTimeout) {
      clearTimeout(this.longLoadingReportTimeout);
    }
  };

  public readonly startLoading = (
    options: {
      bg?: BlockingLoadingOverlayState["bg"];
      fadeIn?: boolean;
      title?: string | TranslationKey;
      message?: string | TranslationKey;
    } = {}
  ) => {
    this.loadingCounter++;

    this.emit({
      ...this.state,
      error: undefined,
      status: "loading",
      bg: options.bg ?? "branded",
      fadeIn: options.fadeIn ?? true,
      title: options.title ?? "",
      message: options.message ?? "",
      progress: undefined,
      progressIndeterminate: undefined,
      progressLabel: undefined,
      onCancel: undefined
    });

    if (options.title) {
      addSentryBreadcrumb(
        "loading.overlay",
        `Loading started: ${options.title}`
      );
    }

    if (this.longLoadingReportTimeout) {
      clearTimeout(this.longLoadingReportTimeout);
    }

    this.longLoadingReportTimeout = setTimeout(() => {
      addSentryBreadcrumb(
        "loading.overlay",
        `Loading has been active for over 15 seconds`
      );
    }, 15000);

    return [this.loadingSuccess, this.loadingError];
  };

  public readonly callErrorRetry = () => {
    if (this.state.error?.retry) {
      this.state.error.retry();
    }
  };

  public readonly callOnCancel = () => {
    if (this.state.error?.onCancel) {
      this.state.error.onCancel();
    }
  };

  public readonly updateProgress = (progress: number, label?: string): void => {
    if (this.state.status === "loading") {
      this.emit({
        ...this.state,
        progress: Math.min(Math.max(progress, 0), 100), // Clamp between 0-100
        progressLabel: label,
        progressIndeterminate: false
      });
    }
  };

  public readonly startLoadingWithProgress = (
    options: {
      bg?: BlockingLoadingOverlayState["bg"];
      fadeIn?: boolean;
      title?: string | TranslationKey;
      message?: string | TranslationKey;
      onCancel?: () => void; // Optional cancel callback
    } = {}
  ) => {
    this.loadingCounter++;

    this.emit({
      ...this.state,
      error: undefined,
      status: "loading",
      bg: options.bg ?? "branded",
      fadeIn: options.fadeIn ?? true,
      title: options.title ?? "",
      message: options.message ?? "",
      progress: 0,
      progressIndeterminate: false,
      progressLabel: undefined,
      onCancel: options.onCancel
    });

    return [this.loadingSuccess, this.loadingError];
  };

  public readonly callCancel = () => {
    if (this.state.onCancel) {
      this.state.onCancel();
    }
  };

  public readonly endAllLoadings = () => {
    addSentryBreadcrumb("loading.overlay", "Finishing all loading states");
    this.loadingCounter = 0;
    this.emit({
      ...this.state,
      status: "none",
      title: "",
      message: ""
    });
  };
}

const BlockingLoadingOverlayController = new BlockingLoadingOverlayBloc();
export default BlockingLoadingOverlayController;
